import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { IconArrowLeft } from '@shared/ui/icons';
import { Button } from '@shared/ui/Button';
import { Input, Label, Field } from '@shared/ui/Input';
import { AddressFields, cleanAddress, formatAddress } from './AddressFields';
import { useAgentPermission } from '@entities/user';
import type { LogistEdoBox, LogistPostalAddress } from '@entities/logistics';
import {
  useListLogistEdoBoxesQuery,
  useCreateLogistEdoBoxMutation,
  useLookupLogistEdoBoxMutation,
  useActivateLogistEdoBoxMutation,
  useDeactivateLogistEdoBoxMutation,
  useSetLogistEdoBoxRequisitesMutation,
} from '@shared/api';

/**
 * ОСНОВА.Логист — справочник ящиков ЭДО контрагентов (§8.2, ADR-0017 §10).
 *
 * Формат ЭТрН идентифицирует стороны перевозки ящиком у оператора, а не
 * наименованием. Основной путь наполнения — поиск у оператора по ИНН: он знает
 * и ящик, и зарегистрирован ли контрагент в Логистике. Ручной ввод — резерв.
 */

const Wrap = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const Back = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const Title = styled.h1`
  margin: 0 0 ${({ theme }) => theme.spacing(2)};
  font-size: 22px;
  font-weight: 600;
`;

const Subtitle = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  max-width: 820px;
`;

const Card = styled.div`
  padding: ${({ theme }) => theme.spacing(5)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.surface};
  margin-bottom: ${({ theme }) => theme.spacing(5)};
  max-width: 860px;
`;

const CardTitle = styled.h2`
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
  font-size: 16px;
  font-weight: 600;
`;

const Row = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  align-items: flex-end;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

const Th = styled.th`
  text-align: left;
  padding: ${({ theme }) => theme.spacing(2)};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: 600;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: ${({ theme }) => theme.spacing(2)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  vertical-align: top;
`;

/** Второстепенная строка в ячейке — адрес под контактами. */
const Muted = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SmallBtn = styled.button`
  font-size: 12px;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
`;

const Badge = styled.span<{ $tone: 'ok' | 'warn' }>`
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(1.5)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  color: #fff;
  background: ${({ theme, $tone }) =>
    $tone === 'ok' ? theme.colors.status.success : theme.colors.status.warning};
`;

const Mono = styled.code`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  word-break: break-all;
`;

const Msg = styled.div<{ $error?: boolean }>`
  margin-top: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme, $error }) =>
    $error ? theme.colors.status.danger : theme.colors.text.secondary};
`;

function apiErr(e: unknown): string {
  if (e && typeof e === 'object' && 'data' in e) {
    const d = (e as { data?: unknown }).data;
    if (d && typeof d === 'object' && 'error' in d) return String((d as { error: unknown }).error);
  }
  return 'ошибка запроса';
}

export function LogistEdoBoxes() {
  const { can_write: canWrite } = useAgentPermission('logist');
  const [search, setSearch] = useState('');
  const [onlyActive, setOnlyActive] = useState(true);

  const { data, isLoading } = useListLogistEdoBoxesQuery({
    query: search || undefined,
    active: onlyActive,
  });

  const [lookup, lookupState] = useLookupLogistEdoBoxMutation();
  const [create, createState] = useCreateLogistEdoBoxMutation();
  const [activate] = useActivateLogistEdoBoxMutation();
  const [deactivate] = useDeactivateLogistEdoBoxMutation();

  const [lookupInn, setLookupInn] = useState('');
  const [lookupKpp, setLookupKpp] = useState('');
  const [manual, setManual] = useState({ inn: '', kpp: '', box_id: '', name: '' });
  const [msg, setMsg] = useState<{ text: string; error?: boolean } | null>(null);
  /** Карточка, у которой правим реквизиты для накладной. */
  const [reqEdit, setReqEdit] = useState<{
    id: string;
    name: string;
    contact_phone: string;
    contact_email: string;
    address: LogistPostalAddress;
  } | null>(null);
  const [saveRequisites, saveRequisitesState] = useSetLogistEdoBoxRequisitesMutation();

  function startEditRequisites(box: LogistEdoBox) {
    setReqEdit({
      id: box.id,
      name: box.name ?? box.inn,
      contact_phone: box.contact_phone ?? '',
      contact_email: box.contact_email ?? '',
      address: box.address ?? {},
    });
    setMsg(null);
  }

  async function submitRequisites() {
    if (!reqEdit) return;
    try {
      await saveRequisites({
        id: reqEdit.id,
        contact_phone: reqEdit.contact_phone.trim() || null,
        contact_email: reqEdit.contact_email.trim() || null,
        address: cleanAddress(reqEdit.address),
      }).unwrap();
      setReqEdit(null);
      setMsg({ text: 'Реквизиты сохранены.' });
    } catch {
      setMsg({ text: 'Не удалось сохранить реквизиты.', error: true });
    }
  }

  const items: LogistEdoBox[] = data?.items ?? [];

  async function onLookup() {
    setMsg(null);
    try {
      const res = await lookup({
        inn: lookupInn.trim(),
        kpp: lookupKpp.trim() || undefined,
      }).unwrap();
      const n = res.items?.length ?? 0;
      setMsg({
        text: n
          ? `Оператор вернул записей: ${n}. Сохранены в справочник.`
          : 'Оператор не нашёл организацию с таким ИНН.',
      });
      setLookupInn('');
      setLookupKpp('');
    } catch (e) {
      setMsg({ text: apiErr(e), error: true });
    }
  }

  async function onCreate() {
    setMsg(null);
    try {
      await create({
        inn: manual.inn.trim(),
        kpp: manual.kpp.trim() || null,
        box_id: manual.box_id.trim(),
        name: manual.name.trim() || null,
      }).unwrap();
      setManual({ inn: '', kpp: '', box_id: '', name: '' });
      setMsg({ text: 'Запись сохранена.' });
    } catch (e) {
      setMsg({ text: apiErr(e), error: true });
    }
  }

  return (
    <Wrap>
      <Back to="/agents/logist">
        <IconArrowLeft /> ОСНОВА.Логист
      </Back>
      <Title>Ящики ЭДО контрагентов</Title>
      <Subtitle>
        Электронная накладная определяет стороны перевозки не названием компании, а её ящиком
        у оператора. Поэтому для каждого контрагента нужен ИНН и найденный по нему ящик — без
        этой пары накладную выпустить нельзя. Проще всего нажать «Найти у оператора»: он
        подставит и ящик, и признак того, подключён ли контрагент к электронным накладным.
      </Subtitle>

      {canWrite && (
        <Card>
          <CardTitle>Найти у оператора по ИНН</CardTitle>
          <Row>
            <Field>
              <Label>ИНН</Label>
              <Input
                value={lookupInn}
                onChange={(e) => setLookupInn(e.target.value)}
                placeholder="7206010213"
              />
            </Field>
            <Field>
              <Label>КПП (филиал, необязательно)</Label>
              <Input
                value={lookupKpp}
                onChange={(e) => setLookupKpp(e.target.value)}
                placeholder="775101001"
              />
            </Field>
            <Button onClick={onLookup} disabled={!lookupInn.trim() || lookupState.isLoading}>
              {lookupState.isLoading ? 'Ищем…' : 'Найти у оператора'}
            </Button>
          </Row>
          {msg && <Msg $error={msg.error}>{msg.text}</Msg>}
        </Card>
      )}

      {canWrite && (
        <Card>
          <CardTitle>Добавить вручную</CardTitle>
          <Subtitle>
            Запасной путь, если оператор недоступен, а идентификатор ящика известен из
            переписки. Это длинный код вида 8c6675be-2a1b-…, а не номер вида 2ВМ-ИНН-… —
            последний обозначает участника обмена и сюда не подходит.
          </Subtitle>
          <Row>
            <Field>
              <Label>ИНН</Label>
              <Input
                value={manual.inn}
                onChange={(e) => setManual({ ...manual, inn: e.target.value })}
              />
            </Field>
            <Field>
              <Label>КПП</Label>
              <Input
                value={manual.kpp}
                onChange={(e) => setManual({ ...manual, kpp: e.target.value })}
              />
            </Field>
            <Field>
              <Label>Идентификатор ящика</Label>
              <Input
                value={manual.box_id}
                onChange={(e) => setManual({ ...manual, box_id: e.target.value })}
                placeholder="8c6675be-2a1b-4593-96c5-92cf6ccb6b9e"
              />
            </Field>
            <Field>
              <Label>Название</Label>
              <Input
                value={manual.name}
                onChange={(e) => setManual({ ...manual, name: e.target.value })}
              />
            </Field>
            <Button
              onClick={onCreate}
              disabled={!manual.inn.trim() || !manual.box_id.trim() || createState.isLoading}
            >
              Сохранить
            </Button>
          </Row>
        </Card>
      )}

      {reqEdit && (
        <Card>
          <CardTitle>Реквизиты для накладной: {reqEdit.name}</CardTitle>
          <Subtitle>
            Телефон, почта и адрес стороны обязательны по формату электронной накладной.
            Оператор их не знает — он отдаёт только название и ИНН. Заполняются один раз
            на контрагента и подставляются во все его накладные.
          </Subtitle>
          <Row>
            <Field>
              <Label>Телефон</Label>
              <Input
                value={reqEdit.contact_phone}
                placeholder="+73456200000"
                onChange={(e) => setReqEdit({ ...reqEdit, contact_phone: e.target.value })}
              />
            </Field>
            <Field>
              <Label>Электронная почта</Label>
              <Input
                value={reqEdit.contact_email}
                onChange={(e) => setReqEdit({ ...reqEdit, contact_email: e.target.value })}
              />
            </Field>
          </Row>
          <AddressFields
            value={reqEdit.address}
            onChange={(address) => setReqEdit({ ...reqEdit, address })}
          />
          <Row style={{ marginTop: 12 }}>
            <Button onClick={submitRequisites} disabled={saveRequisitesState.isLoading}>
              Сохранить
            </Button>
            <Button $variant="secondary" onClick={() => setReqEdit(null)}>
              Отмена
            </Button>
          </Row>
        </Card>
      )}

      <Row style={{ marginBottom: 16 }}>
        <Field>
          <Label>Поиск по ИНН или названию</Label>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} />
        </Field>
        <SmallBtn onClick={() => setOnlyActive((v) => !v)}>
          {onlyActive ? 'Показать в том числе скрытые' : 'Только используемые'}
        </SmallBtn>
      </Row>

      {isLoading ? (
        <Msg>Загрузка…</Msg>
      ) : items.length === 0 ? (
        <Msg>Пока пусто. Начните с поиска по ИНН.</Msg>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Контрагент</Th>
              <Th>ИНН / КПП</Th>
              <Th>Ящик</Th>
              <Th>Электронные накладные</Th>
              <Th>Реквизиты для накладной</Th>
              <Th>Откуда</Th>
              {canWrite && <Th />}
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} style={{ opacity: it.is_active ? 1 : 0.5 }}>
                <Td>
                  {it.name ?? '—'}
                  {it.is_roaming && (
                    <div>
                      <Badge $tone="warn">у другого оператора</Badge>
                    </div>
                  )}
                </Td>
                <Td>
                  {it.inn}
                  {it.kpp ? ` / ${it.kpp}` : ''}
                  {it.fns_participant_id && (
                    <div>
                      <Mono>{it.fns_participant_id}</Mono>
                    </div>
                  )}
                </Td>
                <Td>
                  <Mono>{it.box_id}</Mono>
                </Td>
                <Td>
                  {/* Ключевой сигнал: контрагент без подключения документ не получит,
                      сколько бы раз мы его ни отправляли. */}
                  {it.is_logistics ? (
                    <Badge $tone="ok">подключён</Badge>
                  ) : (
                    <Badge $tone="warn">не подключён</Badge>
                  )}
                </Td>
                <Td>
                  {/* Без контактов и адреса накладная не пройдёт проверку
                      формата, а оператор их не знает — заполняем сами. */}
                  {it.contact_phone || it.contact_email || it.address ? (
                    <>
                      <div>{[it.contact_phone, it.contact_email].filter(Boolean).join(' · ')}</div>
                      {it.address && <Muted>{formatAddress(it.address)}</Muted>}
                    </>
                  ) : (
                    <Badge $tone="warn">не заполнены</Badge>
                  )}
                </Td>
                <Td>{it.source === 'operator' ? 'от оператора' : 'вручную'}</Td>
                {canWrite && (
                  <Td>
                    <SmallBtn onClick={() => startEditRequisites(it)}>Реквизиты</SmallBtn>
                    {it.is_active ? (
                      <SmallBtn onClick={() => deactivate(it.id)}>Скрыть</SmallBtn>
                    ) : (
                      <SmallBtn onClick={() => activate(it.id)}>Вернуть</SmallBtn>
                    )}
                  </Td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Wrap>
  );
}
